import React from "react";
import { ShieldCheck, Layers, Lock, UserCheck } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal/ScrollReveal";

const TECH = [
  { name: "Groq Whisper Large v3", role: "Transcrição PT-BR em tempo real", letter: "G", bg: "#F18859" },
  { name: "Google Gemini 2.5 Flash", role: "Estruturação clínica e geração de prontuário", letter: "Ge", bg: "#7B417B" },
  { name: "FastAPI + Python", role: "Backend de alta performance assíncrono", letter: "Fa", bg: "#4C356B" },
  { name: "MongoDB", role: "Armazenamento seguro de dados clínicos no Brasil", letter: "Mo", bg: "#9E8FB8" },
  { name: "React 18", role: "Interface web moderna e responsiva", letter: "Re", bg: "#C4B8D8" },
];

const PRIVACY = [
  { icon: <UserCheck size={18} />, title: "Isolamento de dados por usuário", body: "Cada profissional acessa apenas os próprios prontuários. Tokens JWT por sessão." },
  { icon: <Lock size={18} />, title: "Sem acesso cruzado entre profissionais", body: "Mesmo dentro de uma clínica, dados são compartimentados por padrão." },
  { icon: <ShieldCheck size={18} />, title: "Conformidade com o Art. 11 da LGPD", body: "Dados sensíveis de saúde tratados conforme o artigo específico de saúde da LGPD." },
  { icon: <Layers size={18} />, title: "Logs de auditoria em cada aprovação", body: "Quem aprovou, quando, e qual era o conteúdo no momento da aprovação." },
];

export default function TechnologyPage() {
  return (
    <>
      <section className="page-intro" data-testid="page-intro">
        <div className="container">
          <ScrollReveal>
            <span className="eyebrow">TECNOLOGIA</span>
            <h1 className="h-1">A tecnologia por trás do Akali AI</h1>
            <p className="body-lg page-intro-sub">
              Arquitetura construída para saúde: precisão, privacidade e
              conformidade por design.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-surface" data-testid="section-stack">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">STACK TÉCNICO</span>
              <h2 className="h-2">As peças que formam o copiloto</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="tech-grid" data-testid="tech-grid">
              {TECH.map((t, i) => (
                <div className="glass tech-card glass-hover" key={t.name} data-testid={`tech-card-${i}`}>
                  <div className="tech-dot" style={{ background: t.bg }}>
                    {t.letter}
                  </div>
                  <h3 className="tech-name">{t.name}</h3>
                  <p className="tech-role">{t.role}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-deep" data-testid="section-human-review">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">DECISÃO DE ARQUITETURA</span>
              <h2 className="h-2">Por que revisão humana obrigatória?</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="prose">
              <p>
                Em saúde, modelos de IA generativa têm uma característica
                inegociável: <strong>podem errar de forma plausível</strong>.
                Um prontuário escrito por IA pode conter um diagnóstico
                coerente em forma, mas incorreto em substância. Em fisioterapia
                — onde o plano terapêutico depende de avaliação funcional
                refinada — esse risco é amplificado.
              </p>
              <p>
                A Resolução COFFITO 414/2012 estabelece que o prontuário
                fisioterápico é documento de responsabilidade do profissional
                habilitado. A ANVISA RDC 657/2022, sobre Software como
                Dispositivo Médico (SaMD), reforça que sistemas de apoio à
                decisão devem manter o ser humano como elemento final e
                vinculante da cadeia clínica.
              </p>
              <p>
                Por isso, o Akali AI implementa a revisão humana não como uma
                preferência configurável, mas como uma <strong>restrição
                arquitetural</strong>: o backend simplesmente não possui
                endpoint para salvar um prontuário sem aprovação explícita.
                Não é uma escolha de UX — é uma garantia de design.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-surface" data-testid="section-quality">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">CONTROLE DE QUALIDADE</span>
              <h2 className="h-2">Sistema de avaliação de qualidade</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="prose">
              <p>
                Todo prontuário gerado passa por avaliação automática de
                qualidade em <strong>5 dimensões clínicas</strong> antes de
                chegar para revisão do profissional. Prontuários que não
                atingem o limiar de qualidade são regenerados automaticamente.
              </p>
              <p>
                Isso garante que o profissional receba sempre a melhor versão
                possível para revisão — economizando tempo de edição e
                reduzindo o atrito da etapa final do fluxo.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-deep" data-testid="section-privacy">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">PRIVACIDADE POR ARQUITETURA</span>
              <h2 className="h-2">Quatro camadas de proteção</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="value-grid" style={{ maxWidth: 920, margin: "0 auto" }}>
              {PRIVACY.map((p, i) => (
                <div
                  key={p.title}
                  className="glass value-card glass-hover"
                  data-testid={`privacy-card-${i}`}
                  style={{ gridColumn: i < 2 ? "auto" : "auto" }}
                >
                  <div className="step-icon">{p.icon}</div>
                  <h3 className="value-card-title">{p.title}</h3>
                  <p className="value-card-body">{p.body}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
}
